import React, { Fragment, useState, useEffect } from "react";
import { Row, Col, Card, Table, Button, Modal, Form } from "react-bootstrap";
import { useParams, Link, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import api from "../../../api";
import * as XLSX from "xlsx";

const WorkOrderGantt = () => {
 const { id } = useParams();
 const [searchParams] = useSearchParams();
const projectFromUrl = searchParams.get("project") || "";
 const [groups, setGroups] = useState([]);
 const [loading, setLoading] = useState(true);

 const [showGroupModal, setShowGroupModal] = useState(false);
 const [showSubModal, setShowSubModal] = useState(false);

 const [editGroup, setEditGroup] = useState(null);
 const [editSub, setEditSub] = useState(null);

 const [newGroup, setNewGroup] = useState({ task: "", remarks: "" });
 const [newSub, setNewSub] = useState({
 groupId: null,
 task: "",
 start: "",
 end: "",
 remarks: "",
 });

  // Format date for display
  const formatDate = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "";
      return dt.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

 // Load data
 const fetchGantt = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/gantt-chart/${id}`);
 if (res.data.success) {
 setGroups(res.data.data || []);
 } else {
 swal("Error", res.data.message || "Failed to load gantt", "error");
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to load gantt data", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchGantt();
 }, [id]);

 const getNextGroupLetter = () => {
 if (!groups.length) return "A";
 const last = groups[groups.length - 1].group;
 return String.fromCharCode(last.charCodeAt(0) + 1);
 };

 const handleAddGroup = async () => {
 if (!newGroup.task.trim()) {
 return swal("Warning", "Task title is required", "warning");
 }

 const payload = {
 workOrderId: id,
 group: getNextGroupLetter(),
 task: newGroup.task,
 remarks: newGroup.remarks,
 };

 try {
 const res = await api.post(`/gantt-chart/${id}/groups`, payload);

 if (res.data.success) {
 swal("Success", "Group added", "success");
 setShowGroupModal(false);
 setNewGroup({ task: "", remarks: "" });
 fetchGantt();
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to add group", "error");
 }
 };

 const handleAddSub = async () => {
 if (!newSub.task.trim() || !newSub.start || !newSub.end) {
 if (new Date(newSub.end) < new Date(newSub.start)) {
 return swal("Warning", "End date must be after start date", "warning");
}
 return swal("Warning", "Fill all required fields", "warning");
 }

 const payload = {
 workOrderId: id,
 groupId: newSub.groupId,
 task: newSub.task,
 start: newSub.start,
 end: newSub.end,
 remarks: newSub.remarks,
 };

 try {
 const res = await api.post(`/gantt-chart/${id}/sub`, payload);

 if (res.data.success) {
 swal("Success", "Sub task added", "success");
 setShowSubModal(false);
 setNewSub({ groupId: null, task: "", start: "", end: "", remarks: "" });
 fetchGantt();
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to add sub task", "error");
 }
 };

 const calcGroupDates = (subs = [], group = {}) => {
 if (!subs.length) {
 if (!group.start || !group.end) return {};
 return {
 start: formatDate(group.start),
 end: formatDate(group.end),
 days: group.days || Math.ceil((new Date(group.end) - new Date(group.start)) / 86400000) + 1,
 };
 }

 const starts = subs.map((s) => new Date(s.start));
 const ends = subs.map((s) => new Date(s.end));

 const start = new Date(Math.min(...starts));
 const end = new Date(Math.max(...ends));
 const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

 return {
 start: formatDate(start),
 end: formatDate(end),
 days,
 };
 };

 // =============================
 // Delete Group
 // =============================
 const handleDeleteGroup = async (groupId) => {
 swal({
 title: "Are you sure?",
 text: "This will delete the group and all sub tasks!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (yes) => {
 if (yes) {
 try {
 const res = await api.delete(`/gantt-chart/item/${groupId}`);
 if (res.data.success) {
 swal("Deleted!", "Group deleted", "success");
 fetchGantt();
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to delete group", "error");
 }
 }
 });
 };

 // =============================
 // Delete Sub Task
 // =============================
 const handleDeleteSub = async (subId) => {
 swal({
 title: "Are you sure?",
 text: "This sub task will be deleted!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (yes) => {
 if (yes) {
 try {
 const res = await api.delete(`/gantt-chart/item/${subId}`);
 if (res.data.success) {
 swal("Deleted!", "Sub task deleted", "success");
 fetchGantt();
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to delete sub task", "error");
 }
 }
 });
 };

 // =============================
 // Update Group
 // =============================
 const updateGroup = async () => {
 try {
 const res = await api.put(`/gantt-chart/${id}/groups/${editGroup._id}`, {
 task: editGroup.task,
 remarks: editGroup.remarks,
 });

 if (res.data.success) {
 swal("Success", "Group updated", "success");
 setEditGroup(null);
 fetchGantt();
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to update group", "error");
 }
 };

 // =============================
 // Update Sub Task
 // =============================
 const updateSub = async () => {
 try {
 const res = await api.put(`/gantt-chart/${id}/sub/${editSub._id}`, {
 task: editSub.task,
 start: editSub.start,
 end: editSub.end,
 remarks: editSub.remarks,
 });

 if (res.data.success) {
 swal("Success", "Sub updated", "success");
 setEditSub(null);
 fetchGantt();
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to update sub task", "error");
 }
 };

 // =============================
 // Excel Export
 // =============================
 const downloadExcel = () => {
 const rows = [];

 groups.forEach((g) => {
 const grp = calcGroupDates(g.subs || [], g);

 rows.push({
 group: g.group,
 groupTask: g.task,
 subCode: "",
 subTask: "",
 start: grp.start || "",
 end: grp.end || "",
 days: grp.days || "",
 status: g.status || "Not Started",
 completionPercentage: g.completionPercentage || 0,
 remarks: g.remarks || "",
 });

 g.subs?.forEach((s, idx) => {
 rows.push({
 group: g.group,
 groupTask: "",
 subCode: idx + 1,
 subTask: s.task,
 start: formatDate(s.start),
 end: formatDate(s.end),
 days: s.days || "",
 remarks: s.remarks || "",
 });
 });
 });

 if (!rows.length) return swal("Warning", "No tasks to export", "warning");

 const ws = XLSX.utils.json_to_sheet(rows);
 const wb = XLSX.utils.book_new();
 XLSX.utils.book_append_sheet(wb, ws, "Gantt");
 XLSX.writeFile(wb, `Gantt-${id}.xlsx`);
 };

 // =============================
 // Excel Import
 // =============================
 const importExcel = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 const reader = new FileReader();
 reader.onload = async (evt) => {
 try {
 const workbook = XLSX.read(evt.target.result, { type: "binary", cellDates: true });
 const sheet = workbook.SheetNames[0];
 const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], { defval: "" });

 const structured = [];
 let currentGroup = null;

 rows.forEach((r) => {
 // Helper to get case-insensitive value
 const getVal = (...keys) => {
 for (const k of keys) {
 if (r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "") {
 return r[k];
 }
 }
 return "";
 };

 const group = getVal("group", "Group", "GROUP");
 const groupTask = getVal("groupTask", "GroupTask", "Group Task", "group_task", "GROUP TASK");
 const subTask = getVal("subTask", "SubTask", "Sub Task", "sub_task", "SUB TASK");
 const generalTask = getVal("task", "Task", "TASK");
 const start = getVal("start", "Start", "START");
 const end = getVal("end", "End", "END");
 const remarks = getVal("remarks", "Remarks", "REMARKS");
 const days = getVal("days", "Days", "DAYS", "No. of Days", "no_of_days");

 const isGroupRow = groupTask || (!subTask && generalTask && !r.code && !r.Code && !r.subCode && !r.SubCode);

 if (group && isGroupRow) {
 currentGroup = {
 group: String(group).trim(),
 task: String(groupTask || generalTask).trim(),
 start: formatDate(start),
 end: formatDate(end),
 days: days ? Number(days) : null,
 remarks: String(remarks || "").trim(),
 subs: [],
 };
 structured.push(currentGroup);
 } else if (subTask || (generalTask && currentGroup)) {
 const taskName = subTask || generalTask;
 if (currentGroup && taskName) {
 currentGroup.subs.push({
 task: String(taskName).trim(),
 start: formatDate(start),
 end: formatDate(end),
 days: days ? Number(days) : null,
 remarks: String(remarks || "").trim(),
 });
 }
 }
 });

 if (!structured.length) {
 return swal("Warning", "No valid group tasks found in Excel file. Please use the Sample Excel template.", "warning");
 }

 const res = await api.post(`/gantt-chart/${id}/import-rows`, {
 data: structured,
 });

 if (res.data.success) {
 swal("Success", "Excel Imported Successfully", "success");
 fetchGantt();
 } else {
 swal("Error", res.data.message || "Failed to import Excel", "error");
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Invalid Excel format: " + (err.message || "Could not parse"), "error");
 } finally {
 e.target.value = "";
 }
 };

 reader.readAsBinaryString(file);
 };

 // =============================
 // Sample Excel Download
 // =============================
 const downloadSampleExcel = () => {
 const sample = [
 {
 group: "A",
 groupTask: "Mobilization",
 subCode: "",
 subTask: "",
 start: "2025-01-01",
 end: "2025-01-05",
 days: 5,
 remarks: "",
 },
 {
 group: "A",
 groupTask: "",
 subCode: 1,
 subTask: "Site Setup",
 start: "2025-01-02",
 end: "2025-01-03",
 days: 2,
 remarks: "",
 },
 ];

 const ws = XLSX.utils.json_to_sheet(sample);
 const wb = XLSX.utils.book_new();
 XLSX.utils.book_append_sheet(wb, ws, "Sample");
 XLSX.writeFile(wb, "Gantt-Sample.xlsx");
 };

 return (
 <Fragment>
 <PageTitle motherMenu="Work Orders" activeMenu="Gantt Chart" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Gantt Chart Builder</Card.Title>
 <Link
 to={
 projectFromUrl
 ? `/work-orders?project=${projectFromUrl}`
 : "/work-orders"
 }
 className="btn btn-secondary"
>
 Back
</Link>
 </Card.Header>

 <Card.Body>
 <Button variant="primary" onClick={() => setShowGroupModal(true)}>+ Add Group Task</Button>
 <Button variant="warning" className="ms-2" onClick={downloadExcel}>Download Excel</Button>
 <Button variant="secondary" className="ms-2" onClick={downloadSampleExcel}>Sample Excel</Button>

 <label className="btn btn-info ms-2 mb-0">
 Upload Excel
 <input type="file" hidden accept=".xlsx,.xls" onChange={importExcel} />
 </label>

 <hr />

 {loading ? (
 <div className="text-center py-3">Loading...</div>
 ) : (
 <Table bordered responsive>
 <thead style={{ background: "#e4e4e4", fontWeight: 600 }}>
 <tr>
 <th>Code</th>
 <th>Task</th>
 <th>Days</th>
 <th>Start</th>
 <th>End</th>
 <th>Status</th>
 <th>Remarks</th>
 <th style={{ width: "150px" }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {groups.length === 0 ? (
 <tr>
 <td colSpan="8" className="text-center text-muted py-3">No tasks added.</td>
 </tr>
 ) : (
 groups.map((g) => {
 const grp = calcGroupDates(g.subs || [], g);

 return (
 <Fragment key={g._id}>
 <tr style={{ background: "#fff7cc", fontWeight: 600 }}>
 <td>{g.group}</td>
 <td>{g.task}</td>
 <td>{grp.days || ""}</td>
 <td>{grp.start || ""}</td>
 <td>{grp.end || ""}</td>
 <td>
 {g.status || "Not Started"}
 <small className="text-muted ms-1">
 ({g.completionPercentage || 0}%)
 </small>
 </td>
 <td>{g.remarks || ""}</td>
 <td className="d-flex gap-2">
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => setEditGroup(g)}
 >
 Edit
 </Button>
 <Button
 size="sm"
 variant="outline-danger"
 onClick={() => handleDeleteGroup(g._id)}
 >
 Delete
 </Button>
 </td>
 </tr>

 {g.subs?.map((s, idx) => (
 <tr key={s._id}>
 <td>{g.group}.{idx + 1}</td>
 <td>{s.task}</td>
 <td>{s.days || ""}</td>
 <td>{formatDate(s.start)}</td>
 <td>{formatDate(s.end)}</td>
 <td className="text-muted">—</td>
 <td>{s.remarks}</td>
 <td className="d-flex gap-2">
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => setEditSub({ ...s, groupId: g._id })}
 >
 Edit
 </Button>
 <Button
 size="sm"
 variant="outline-danger"
 onClick={() => handleDeleteSub(s._id)}
 >
 Delete
 </Button>
 </td>
 </tr>
 ))}

 <tr>
 <td colSpan="8">
 <Button
 size="sm"
 variant="success"
 onClick={() => {
 setNewSub((p) => ({ ...p, groupId: g._id }));
 setShowSubModal(true);
 }}
 >
 + Add Sub Task
 </Button>
 </td>
 </tr>
 </Fragment>
 );
 })
 )}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Add Group Modal */}
 <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
 <Modal.Header closeButton><Modal.Title>Add Group Task</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group>
 <Form.Label>Task *</Form.Label>
 <Form.Control
 value={newGroup.task}
 onChange={(e) => setNewGroup((p) => ({ ...p, task: e.target.value }))}
 />
 </Form.Group>
 <Form.Group className="mt-2">
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={newGroup.remarks}
 onChange={(e) => setNewGroup((p) => ({ ...p, remarks: e.target.value }))}
 />
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowGroupModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleAddGroup}>Add</Button>
 </Modal.Footer>
 </Modal>

 {/* Add Sub Modal */}
 <Modal show={showSubModal} onHide={() => setShowSubModal(false)}>
 <Modal.Header closeButton><Modal.Title>Add Sub Task</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group>
 <Form.Label>Task *</Form.Label>
 <Form.Control
 value={newSub.task}
 onChange={(e) => setNewSub((p) => ({ ...p, task: e.target.value }))}
 />
 </Form.Group>

 <Row className="mt-2">
 <Col>
 <Form.Label>Start *</Form.Label>
 <Form.Control
 type="date"
 value={newSub.start}
 onChange={(e) => setNewSub((p) => ({ ...p, start: e.target.value }))}
 />
 </Col>
 <Col>
 <Form.Label>End *</Form.Label>
 <Form.Control
 type="date"
 value={newSub.end}
 onChange={(e) => setNewSub((p) => ({ ...p, end: e.target.value }))}
 />
 </Col>
 </Row>

 <Form.Group className="mt-2">
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={newSub.remarks}
 onChange={(e) => setNewSub((p) => ({ ...p, remarks: e.target.value }))}
 />
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowSubModal(false)}>Cancel</Button>
 <Button variant="success" onClick={handleAddSub}>Add</Button>
 </Modal.Footer>
 </Modal>

 {/* Edit Group Modal */}
 <Modal show={!!editGroup} onHide={() => setEditGroup(null)}>
 <Modal.Header closeButton><Modal.Title>Edit Group</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group>
 <Form.Label>Task</Form.Label>
 <Form.Control
 value={editGroup?.task || ""}
 onChange={(e) => setEditGroup((p) => ({ ...p, task: e.target.value }))}
 />
 </Form.Group>
 <Form.Group className="mt-2">
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={editGroup?.remarks || ""}
 onChange={(e) => setEditGroup((p) => ({ ...p, remarks: e.target.value }))}
 />
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setEditGroup(null)}>Cancel</Button>
 <Button variant="primary" onClick={updateGroup}>Save</Button>
 </Modal.Footer>
 </Modal>

 {/* Edit Sub Modal */}
 <Modal show={!!editSub} onHide={() => setEditSub(null)}>
 <Modal.Header closeButton><Modal.Title>Edit Sub Task</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group>
 <Form.Label>Task</Form.Label>
 <Form.Control
 value={editSub?.task || ""}
 onChange={(e) => setEditSub((p) => ({ ...p, task: e.target.value }))}
 />
 </Form.Group>

 <Row className="mt-2">
 <Col>
 <Form.Label>Start</Form.Label>
 <Form.Control
 type="date"
 value={formatDate(editSub?.start)}
 onChange={(e) => setEditSub((p) => ({ ...p, start: e.target.value }))}
 />
 </Col>
 <Col>
 <Form.Label>End</Form.Label>
 <Form.Control
 type="date"
 value={formatDate(editSub?.end)}
 onChange={(e) => setEditSub((p) => ({ ...p, end: e.target.value }))}
 />
 </Col>
 </Row>

 <Form.Group className="mt-2">
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={editSub?.remarks || ""}
 onChange={(e) => setEditSub((p) => ({ ...p, remarks: e.target.value }))}
 />
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setEditSub(null)}>Cancel</Button>
 <Button variant="primary" onClick={updateSub}>Save</Button>
 </Modal.Footer>
 </Modal>

 </Fragment>
 );
};

export default WorkOrderGantt;
